// The card's aggregator (2026-10-08; the openings' average score below zero
// before): the period's openings, by family and colour, counted by the
// scanned games the reader came out of behind — the score out of the
// opening in the "lost a little" or "lost score" bands (`changeBandOf`,
// under −0.50) — most games first, the top five. Registered with the game
// store by this plugin's setup; the store runs it over the last 30 days.
import { TOP_OPENINGS } from '@chess/api-insights';
import { aggregatorId, changeBandOf, type Accumulator, type Aggregator, type Color } from '@chess/api-model';

/** One opening the reader came out of behind. */
export interface BehindOpening {
  /** The family, e.g. "Sicilian Defense". */
  opening: string;
  colour: Color;
  /** Scanned games of the opening and colour the reader came out of behind. */
  behind: number;
  /** Of them, those out at −2.00 or worse. */
  lost: number;
  /** Scanned games of the opening and colour with a score. */
  scored: number;
}

/** What the card shows: the top openings, and how many games the reader came out of behind over all openings. */
export interface LosingOpeningsResult {
  rows: BehindOpening[];
  behind: number;
  scored: number;
}

export class LosingOpeningsAggregator implements Aggregator<LosingOpeningsResult> {
  readonly id = aggregatorId('losing-openings');

  start(): Accumulator<LosingOpeningsResult> {
    const rows = new Map<string, BehindOpening>();
    let behind = 0;
    let scored = 0;
    return {
      add: (g) => {
        if (!g.scanned || g.openingScore === undefined || !g.opening) return;
        scored += 1;
        const key = `${g.opening}|${g.colour}`;
        let row = rows.get(key);
        if (!row) rows.set(key, (row = { opening: g.opening, colour: g.colour, behind: 0, lost: 0, scored: 0 }));
        row.scored += 1;
        const band = changeBandOf(g.openingScore);
        if (band === 'slightly-lost' || band === 'lost') {
          behind += 1;
          row.behind += 1;
          if (band === 'lost') row.lost += 1;
        }
      },
      result: () => ({
        rows: [...rows.values()]
          .filter((r) => r.behind > 0)
          .sort((a, b) => b.behind - a.behind || b.lost - a.lost || a.opening.localeCompare(b.opening))
          .slice(0, TOP_OPENINGS),
        behind,
        scored,
      }),
    };
  }
}
